import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type {
  ReferringCompany,
  CompanyShowcaseResponse,
  CompanyPaginatedResponse
} from '@/types/company'

export const useReferringCompaniesStore = defineStore('referringCompanies', () => {
  const highlightedCompanies = ref<ReferringCompany[]>([])
  const featuredCompanies = ref<ReferringCompany[]>([])
  const regularCompanies = ref<ReferringCompany[]>([])
  const totalCount = ref(0)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const isLoadingShowcase = ref(false)
  const isLoadingRegular = ref(false)
  const error = ref<string | null>(null)

  const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))
  const hasHighlightedCompanies = computed(() => highlightedCompanies.value.length > 0)
  const hasFeaturedCompanies = computed(() => featuredCompanies.value.length > 0)

  const fetchShowcaseCompanies = async () => {
    isLoadingShowcase.value = true
    error.value = null

    try {
      const response = await api.get<CompanyShowcaseResponse>(API_ROUTES.GET_REFERRING_COMPANIES_SHOWCASE)
      highlightedCompanies.value = response.data.highlighted || []
      featuredCompanies.value = response.data.featured || []
      return response.data
    } catch (err: any) {
      error.value =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch companies showcase'
      throw err
    } finally {
      isLoadingShowcase.value = false
    }
  }

  const fetchRegularCompanies = async (page = currentPage.value, ordering?: string) => {
    isLoadingRegular.value = true
    error.value = null

    try {
      const queryParams = new URLSearchParams()
      queryParams.append('page', page.toString())
      queryParams.append('page_size', pageSize.value.toString())
      if (ordering) {
        queryParams.append('ordering', ordering)
      }

      const response = await api.get<CompanyPaginatedResponse>(
        `${API_ROUTES.GET_REFERRING_COMPANIES}?${queryParams.toString()}`
      )

      regularCompanies.value = response.data.results || []
      totalCount.value = response.data.count || 0
      currentPage.value = page
      return response.data
    } catch (err: any) {
      error.value =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch companies'
      throw err
    } finally {
      isLoadingRegular.value = false
    }
  }

  const setPage = (page: number) => {
    currentPage.value = page
  }

  const updateCompany = async (
    companyId: number,
    data: {
      is_featured: boolean
      featured_reason: string | null
      job_board_url: string | null
      job_application_notes: string | null
    }
  ) => {
    error.value = null

    try {
      const response = await api.patch(API_ROUTES.UPDATE_EMPLOYER(companyId), data)
      const updated = response.data

      const updateList = (list: ReferringCompany[]) => {
        const index = list.findIndex((company) => company.id === companyId)
        if (index !== -1) {
          list[index] = { ...list[index], ...updated }
        }
      }

      updateList(highlightedCompanies.value)
      updateList(featuredCompanies.value)
      updateList(regularCompanies.value)

      return updated
    } catch (err: any) {
      error.value =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to update company'
      throw err
    }
  }

  return {
    highlightedCompanies,
    featuredCompanies,
    regularCompanies,
    totalCount,
    currentPage,
    pageSize,
    isLoadingShowcase,
    isLoadingRegular,
    error,
    totalPages,
    hasHighlightedCompanies,
    hasFeaturedCompanies,
    fetchShowcaseCompanies,
    fetchRegularCompanies,
    setPage,
    updateCompany
  }
})
